import React, { useState } from 'react';
import { Text } from 'common-components/typography';
import _ from 'lodash';
import { IRootDispatch, IRootState } from 'stores/rematch/root-store';
import { BottomActionSheet } from './components/details/BottomActionSheet';
import { useDispatch, useSelector } from 'react-redux';
import { ServiceType } from 'utilities/enum-utils';
import RecurringBookingListModal from 'views/bookings-v2/components/listings/components/RecurringBookingListModal';
import BulkApproveWarningModal from 'views/bookings-v2/bookings-table/components/details/action-sheets/BulkApproveWarningModal';
import { IBooking } from 'interfaces/booking-interfaces';
import { HeaderFieldsItem } from './components/details/HeaderFieldsItem';
import BookingBulkRemoveTeamMemberModal from '../components/BookingBulkRemoveTeamMemberModal';
import CancelBulkBookingsModal from '../components/listings/components/CancelBulkBookingsModal';
import { InfiniteScroll } from 'components';
import { useBookings } from 'views/bookings-v2/books-view-context/bookings-view-context';
import { Alert, Box, Button, Checkbox, Stack, Table } from '@good/ui/core';
import { CloudCross } from '@good/icons';
import { BookingsTableEmptyState } from './components/bookings-table-empty-state';
import { useHistory } from 'react-router-dom';
import { BookingsTableSkeleton } from './bookings-table-skeleton';
import { notifications } from '@good/ui/notifications';
import { BookingRow } from './booking-row';
import CommonUtils from 'utilities/common-utils';
import { useBookingsQuery } from '../books-view-context/bookings-query-context';

export const BookingsTable = () => {
  const history = useHistory();
  const [isRecurringListModalOpen, setIsRecurringListModalOpen] = useState<boolean>(false);
  const [selectedBookingItem, setSelectedBookingItem] = useState<unknown | null>(null);
  const [isBulkApprovalWarningModalOpen, setIsBulkApprovalWarningModalOpen] = useState<boolean>(false);
  const [missingPaymentMethodBookings, setMissingPaymentMethodBookings] = useState<unknown[] | null>(null);
  const [bulkActionList, setBulkActionList] = useState<unknown[] | null>(null);
  const [bookingStatus, setBookingStatus] = useState<string>('');
  const [isBookingBulkRemoveTeamMemberModalOpen, setIsBookingBulkRemoveTeamMemberModalOpen] = useState<boolean>(false);
  const [selectedBookingList, setSelectedBookingList] = useState<IBooking[]>([]);
  const [isCancelBookingOpen, setIsCancelBookingOpen] = useState<boolean>(false);
  const [bookingErrors, setBookingErrors] = useState<unknown[]>([]);
  const onChangeShowActionSheet = useBookingsQuery((selector) => selector.onChangeShowActionSheet);
  const onChangeIndeterminateCheck = useBookingsQuery((selector) => selector.onChangeIndeterminateCheck);
  const onChangeCheckAllIndicator = useBookingsQuery((selector) => selector.onChangeCheckAllIndicator);
  const showActionSheet = useBookingsQuery((selector) => selector.showActionSheet);
  const indeterminateCheck = useBookingsQuery((selector) => selector.indeterminateCheck);
  const checkAllIndicator = useBookingsQuery((selector) => selector.checkAllIndicator);
  const fetchBookings = useBookingsQuery((selector) => selector.fetchBookings);
  const refreshListings = useBookings((selector) => selector.refreshListings);
  const pageNumbers = useBookingsQuery((selector) => selector.pageNumbers);
  const pageTimestamps = useBookingsQuery((selector) => selector.pageTimestamps);
  const hasFetchedInitialViews = useBookingsQuery((selector) => selector.hasFetchedInitialViews);
  const pageSize = useBookingsQuery((selector) => selector.pageSize);
  const hasError = useBookingsQuery((selector) => selector.hasError);
  const retry = useBookings((selector) => selector.retry);
  const displayTimezone = useBookings((selector) => selector.displayTimezone);
  const dispatch = useDispatch<IRootDispatch>();
  const setSelectedBookings = dispatch.bookingsStore.setSelectedBookings;
  const doBulkRemoveTeamMembersBookingListing = dispatch.bookingsStore.doBulkRemoveTeamMembersBookingListing;
  const doBulkCancelBookingsBookingListing = dispatch.bookingsStore.doBulkCancelBookingsBookingListing;
  const selectedBookings = useSelector((selector: IRootState) => selector.bookingsStore.selectedBookings);
  const bookingListingActiveTab = useSelector((selector: IRootState) => selector.bookingsStore.bookingListingActiveTab);
  const bookingsList = useSelector((selector: IRootState) => {
    return selector.bookingsStore.bookingsList[selector.bookingsStore.selectedFilterKey] ?? [];
  });
  const isBookingListingLoading = useSelector((selector: IRootState) => selector.bookingsStore.isBookingListingLoading);
  const selectedFilterKey = useSelector((state: IRootState) => state.bookingsStore.selectedFilterKey);
  const bookingsFilter = useSelector((state: IRootState) => state.bookingsStore.bookingsFilter);
  const defaultBookingViews = useSelector((state: IRootState) => state.bookingsStore.defaultBookingViews);
  const displayedBookingListingTabs = useSelector(
    (state: IRootState) => state.bookingsStore.displayedBookingListingTabs,
  );
  const onChangeFilter = useBookings((selector) => selector.onChangeFilter);
  let selectedFilterKey_ = selectedFilterKey ?? 'ALL';
  if (_.isEmpty(selectedFilterKey_)) {
    selectedFilterKey_ = 'ALL';
  }
  const currentFilterConfig = [...defaultBookingViews, ...displayedBookingListingTabs].find(
    (view) => view.customViewId === selectedFilterKey_,
  );
  const searchFilter = CommonUtils.findFilter('search', bookingsFilter);
  if (searchFilter && currentFilterConfig) {
    currentFilterConfig.filterValue.search = searchFilter; // this is wrong or we are missing type info
  }

  const checkItem = (bookingId: string) => {
    const newSelectedBookings = selectedBookings.map((booking) =>
      booking.bookingId === bookingId ? { ...booking, selected: !booking.selected } : { ...booking },
    );
    const someRowsHaveCheck = newSelectedBookings.some((b) => b.selected === true);
    const indeterminateCheck = someRowsHaveCheck && !newSelectedBookings.every((b) => b.selected === true);
    setSelectedBookings(newSelectedBookings);
    onChangeShowActionSheet(someRowsHaveCheck);
    onChangeIndeterminateCheck(indeterminateCheck);
  };

  const _onCheckAll = () => {
    if (_.isEmpty(selectedBookings)) {
      return;
    }

    const newCheckAll = !checkAllIndicator;
    const newSelectedBookings = selectedBookings.map((b) => ({
      ...b,
      selected:
        b.serviceType === ServiceType.INDIVIDUAL || b.serviceType === ServiceType.COORDINATION ? newCheckAll : false,
    }));
    setSelectedBookings(newSelectedBookings);
    onChangeShowActionSheet(newCheckAll);
    onChangeIndeterminateCheck(false);
    onChangeCheckAllIndicator(newCheckAll);
  };

  const onActionDeselect = () => {
    const newSelectedBookings = selectedBookings.map((b) => ({ ...b, selected: false }));
    onChangeShowActionSheet(false);
    onChangeIndeterminateCheck(false);
    onChangeCheckAllIndicator(false);
    setSelectedBookings(newSelectedBookings);
  };

  const fetchMoreBookings = async () => {
    const viewId = bookingListingActiveTab?.customViewId;
    if (!viewId) {
      throw new Error('No active view ID found');
    }
    const nextPage = (pageNumbers[viewId] ?? 1) + 1;
    await fetchBookings({
      pageNumber: nextPage,
      caller: 'BookingsTable.fetchMoreBookings',
      viewId,
    });
  };

  const closeRecurringListModal = () => {
    setIsRecurringListModalOpen(false);
    setSelectedBookingItem(null);
  };

  const openBulkApproveWarningModal = (
    missingPaymentMethodBookings: unknown[],
    bookingList: unknown[],
    bookingErrors: unknown[],
  ) => {
    setIsBookingBulkRemoveTeamMemberModalOpen(true);
    setMissingPaymentMethodBookings(missingPaymentMethodBookings);
    setBulkActionList(bookingList);
    setBookingErrors(bookingErrors);
  };

  const onCloseApproveWarningModal = () => {
    setIsBulkApprovalWarningModalOpen(false);
    refreshListings();
  };

  const onOpenBookingBulkRemoveTeamMemberModal = (bookingList: IBooking[], status: string) => {
    setIsBookingBulkRemoveTeamMemberModalOpen(true);
    setSelectedBookingList(bookingList);
    setBookingStatus(status);
  };

  const onCloseBookingBulkRemoveTeamMemberModal = (removeSuccessfully: boolean) => {
    setIsBookingBulkRemoveTeamMemberModalOpen(false);
    if (removeSuccessfully) {
      refreshListings();
    }
  };

  const onBulkRemoveTeamMemberBookingListing = async () => {
    try {
      const removeTeamMemberFromBookings: unknown[] = [];
      _.forEach(selectedBookingList, (selectedBooking) => {
        removeTeamMemberFromBookings.push({
          bookingId: selectedBooking.bookingId,
          bookingRequestId: selectedBooking.bookingRequestId,
          bookingType: selectedBooking.bookingType,
          supportWorkerId: selectedBooking.workerId ? selectedBooking.workerId : selectedBooking.supportWorkerId,
        });
      });
      const payload = {
        bookingStatus,
        removeTeamMemberFromBookings,
      };
      await doBulkRemoveTeamMembersBookingListing(payload);
      notifications.show({
        color: 'green',
        title: 'Bulk actions successfully completed.',
        message: (
          <Text className='mt-medium'>
            Bulk actions for <span className='text-weight-bold'>Remove Team Members</span> complete.
          </Text>
        ),
        duration: 3,
      });
      return true;
    } catch (e) {
      notifications.show({
        color: 'red',
        title: 'Bulk actions failed',
        message: (
          <Text className='mt-medium'>
            Bulk actions for <span className='text-weight-bold'>Remove Team Members</span> complete has encounter an
            error. Please try again.
          </Text>
        ),
      });
      return false;
    }
  };

  const _onOpenCancelBookingModal = (bookingList: IBooking[], status: string) => {
    setIsCancelBookingOpen(true);
    setSelectedBookingList(bookingList);
    setBookingStatus(status);
  };

  const _onCloseCancelBookingModal = (cancelSuccessfully: boolean) => {
    setIsCancelBookingOpen(false);
    if (cancelSuccessfully) {
      refreshListings();
    }
  };

  const onBulkCancelBookingListing = async (data) => {
    try {
      const cancelBookingsId: string[] = [];
      _.forEach(selectedBookingList, (selectedBooking) => cancelBookingsId.push(selectedBooking.bookingId));
      const payload = {
        bookingStatus: bookingStatus,
        cancelBookingsId,
        ...data,
      };
      await doBulkCancelBookingsBookingListing(payload);
      return true;
    } catch (e) {
      return false;
    }
  };

  if (hasError) {
    return (
      <Box p='xl'>
        <Alert color='red' title='Something went wrong' icon={<CloudCross />}>
          <Stack align='start'>
            <Text>There was an error loading bookings. Please try again.</Text>
            <Button onClick={retry} variant='default'>
              Retry
            </Button>
          </Stack>
        </Alert>
      </Box>
    );
  }

  const pageNumber = pageNumbers[bookingListingActiveTab.customViewId] ?? 1;
  const pageTimestamp = pageTimestamps[bookingListingActiveTab.customViewId] ?? new Date(0);
  const hasMore = bookingsList.length >= pageNumber * pageSize;

  return (
    <>
      <Table className='booking-listing'>
        <Table.Thead>
          <Table.Tr>
            <Table.Th className='nowrap check-all pr-none'>
              <Checkbox onClick={_onCheckAll} checked={checkAllIndicator} indeterminate={indeterminateCheck} />
            </Table.Th>
            <HeaderFieldsItem customFields={currentFilterConfig?.customFields} />
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {!isBookingListingLoading && hasFetchedInitialViews && _.isEmpty(bookingsList) && (
            <Table.Tr>
              <Table.Td colSpan={7}>
                <BookingsTableEmptyState />
              </Table.Td>
            </Table.Tr>
          )}
          <InfiniteScroll
            hasMore={hasMore}
            loadMore={fetchMoreBookings}
            asTableRow
            customLoader={<BookingsTableSkeleton rows={10} />}
            triggerOnLoaderVisible
            initialPage={pageNumber === 0}
          >
            {!_.isEmpty(bookingsList) &&
              bookingsList.map((bookingItem, index) => (
                <BookingRow
                  key={index}
                  currentFilterConfig={currentFilterConfig}
                  bookingItem={bookingItem}
                  onCheckItem={checkItem}
                  onChangeFilter={onChangeFilter}
                />
              ))}
          </InfiniteScroll>
        </Table.Tbody>
      </Table>
      <Box flex={1} bg='var(--mantine-color-body)'>
        &nbsp;
      </Box>
      {showActionSheet && (
        <BottomActionSheet
          selectedBookings={selectedBookings}
          onDeselect={onActionDeselect}
          refreshBookingListings={refreshListings}
          openBulkApproveWarningModal={openBulkApproveWarningModal}
          openBulkRemoveTeamMemberModal={onOpenBookingBulkRemoveTeamMemberModal}
          openCancelBookingModal={_onOpenCancelBookingModal}
        />
      )}
      <RecurringBookingListModal
        history={history}
        isOpen={isRecurringListModalOpen}
        bookingItem={selectedBookingItem}
        closeRecurringBookingListModal={closeRecurringListModal}
        displayTimezone={displayTimezone}
      />
      <BulkApproveWarningModal
        isOpen={isBulkApprovalWarningModalOpen}
        missingPaymentMethodBookings={missingPaymentMethodBookings}
        onCloseModal={onCloseApproveWarningModal}
        hasApprovedBookings={
          missingPaymentMethodBookings && bulkActionList && missingPaymentMethodBookings.length < bulkActionList.length
        }
        history={history}
        bookingErrors={bookingErrors}
      />
      <CancelBulkBookingsModal
        isOpen={isCancelBookingOpen}
        onClose={_onCloseCancelBookingModal}
        onCancel={onBulkCancelBookingListing}
        selectedBookings={selectedBookingList}
        amountItems={selectedBookingList.length}
      />
      <BookingBulkRemoveTeamMemberModal
        isOpen={isBookingBulkRemoveTeamMemberModalOpen}
        onClose={onCloseBookingBulkRemoveTeamMemberModal}
        bookingList={selectedBookingList}
        onRemove={onBulkRemoveTeamMemberBookingListing}
        // portalUser={portalUser}
        // {...state}
        pageSize={pageSize}
        displayTimezone={displayTimezone}
        topHeight={0}
        isLoading={isBookingListingLoading}
        page={pageNumber}
        pageTimestamp={pageTimestamp}
      />
    </>
  );
};
